import React from 'react';
import { AgnesAutocomplete } from '@agnes/ui';
import UtilsMask from '@/utils/UtilsMask';

type Any = any;

interface EmpregadorOption {
  cnpj: string;
  nome: string;
}

// Linha "Todos" no topo da lista: marca/desmarca todos os empregadores de uma vez.
const TODOS = '__todos__';
const OPCAO_TODOS: EmpregadorOption = { cnpj: TODOS, nome: 'Todos' };

interface Props {
  label: string;
  placeholder: string;
  empregadores: Any[];
  value: string[];
  disabled?: boolean;
  noOptionsText?: string;
  className?: string;
  onChange: (cnpjs: string[]) => void;
}

function montarOpcoes(empregadores: Any[]): EmpregadorOption[] {
  const porCnpj = new Map<string, EmpregadorOption>();
  empregadores.forEach((empregador) => {
    const cnpj = empregador.cnpjFilial;
    if (!cnpj || porCnpj.has(cnpj)) return;
    const nome = empregador.socialName || empregador.company || empregador.fantasyName || cnpj;
    porCnpj.set(cnpj, { cnpj, nome });
  });
  return [...porCnpj.values()].sort(
    (a, b) => a.nome.localeCompare(b.nome, 'pt-BR') || a.cnpj.localeCompare(b.cnpj),
  );
}

// Seletor compacto de empregadores (mesmo padrão do AgnesAutocomplete da Central de
// Reprocessamento): uma linha "Razão social - CNPJ" por estabelecimento.
export function DctfEmpregadorSelect({
  label,
  placeholder,
  empregadores,
  value,
  disabled = false,
  noOptionsText,
  className,
  onChange,
}: Props) {
  const opcoes = React.useMemo(() => montarOpcoes(empregadores), [empregadores]);
  const selecionados = new Set(value);
  const valorSelecionado = opcoes.filter((opcao) => selecionados.has(opcao.cnpj));
  const todosSelecionados = opcoes.length > 0 && valorSelecionado.length === opcoes.length;

  const alternar = (opcao: EmpregadorOption) => {
    if (opcao.cnpj === TODOS) {
      onChange(todosSelecionados ? [] : opcoes.map((item) => item.cnpj));
      return;
    }
    const proximos = new Set(selecionados);
    if (proximos.has(opcao.cnpj)) proximos.delete(opcao.cnpj);
    else proximos.add(opcao.cnpj);
    onChange(opcoes.filter((item) => proximos.has(item.cnpj)).map((item) => item.cnpj));
  };

  return (
    <AgnesAutocomplete<EmpregadorOption>
      label={label}
      options={opcoes.length > 0 ? [OPCAO_TODOS, ...opcoes] : opcoes}
      value={valorSelecionado}
      onOptionToggle={alternar}
      getOptionLabel={(opcao) =>
        opcao.cnpj === TODOS ? opcao.nome : `${opcao.nome} - ${UtilsMask.cnpj(opcao.cnpj)}`
      }
      isOptionEqualToValue={(opcao, selecionado) =>
        opcao.cnpj === TODOS ? todosSelecionados : opcao.cnpj === selecionado.cnpj
      }
      placeholder={placeholder}
      noOptionsText={noOptionsText}
      disabled={disabled}
      className={className}
    />
  );
}
